import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const RELAY_URL = process.env.REACT_APP_RELAY_URL || 'http://localhost:3001';

export const AuthProvider = ({ children }) => {
  const [token, setToken]               = useState(localStorage.getItem('nkey_admin_token') || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked]   = useState(false);

  const doVerify = useCallback(async (t) => {
    if (!t) return false;
    try {
      const res = await fetch(`${RELAY_URL}/api/dashboard/verify`, {
        headers: { 'X-Admin-Token': t, 'Content-Type': 'application/json' }
      });
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  // Verify stored token on mount
  useEffect(() => {
    if (!token) {
      setAuthChecked(true);
      return;
    }
    doVerify(token).then(valid => {
      setIsAuthenticated(valid);
      if (!valid) {
        localStorage.removeItem('nkey_admin_token');
        setToken('');
      }
      setAuthChecked(true);
    });
  }, []); // eslint-disable-line

  const login = async (newToken) => {
    const valid = await doVerify(newToken);
    if (!valid) throw new Error('Invalid admin token');
    localStorage.setItem('nkey_admin_token', newToken);
    setToken(newToken);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('nkey_admin_token');
    setToken('');
    setIsAuthenticated(false);
  };

  const fetchAuthorized = async (url, options = {}) => {
    const fullUrl = url.startsWith('http') ? url : `${RELAY_URL}${url}`;
    const headers = {
      ...options.headers,
      'X-Admin-Token': token,
      'Content-Type': 'application/json',
    };
    const response = await fetch(fullUrl, { ...options, headers });
    if (response.status === 401) {
      logout();
      throw new Error('Unauthorized');
    }
    return response;
  };

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, authChecked, login, logout, fetchAuthorized, relayUrl: RELAY_URL }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
